import {useEffect, useRef} from "react"
import {useCanvasSetup} from "./useCanvasSetup"
import {useTileMode} from "./useTileMode"
import {useCropMode} from "./useCropMode"
import {CONSTANTS} from "../constants/constants"
import type {Position, Dimensions, CropperHookReturn} from "../types/types"

interface CanvasRendererProps {
    mainCanvasRef: React.RefObject<HTMLCanvasElement>
    cropper: CropperHookReturn
}

export const useCanvasRenderer = ({
    mainCanvasRef,
    cropper,
}: CanvasRendererProps) => {
    const {setupCanvases} = useCanvasSetup()
    const {renderTile} = useTileMode()
    const {renderCrop} = useCropMode()
    const animationIdRef = useRef<number | null>(null)
    const currentImageRef = useRef<HTMLImageElement | null>(null)

    useEffect(() => {
        if (cropper?.currentImageUrl) {
            const img = new Image()
            img.onload = () => {
                currentImageRef.current = img

                if (cropper.imageState.status === "loaded") {
                    const zoomFactor = cropper.imageState.zoom / 100
                    const position = cropper.imageState.position
                    const dimensions = {
                        width: cropper.imageState.dimensions.width * zoomFactor,
                        height:
                            cropper.imageState.dimensions.height * zoomFactor,
                    }

                    renderFrame(position, dimensions)
                }
            }
            img.src = cropper.currentImageUrl
        } else {
            currentImageRef.current = null
        }
    }, [cropper?.currentImageUrl])

    const renderFrame = (position: Position, dimensions: Dimensions) => {
        if (!cropper) return

        const img =
            cropper.videoElement && !cropper.videoElement.paused
                ? cropper.videoElement
                : currentImageRef.current

        if (!img) return

        const canvasContext = setupCanvases(
            mainCanvasRef.current,
            cropper.cropState.dimensions.width,
            cropper.cropState.dimensions.height
        )

        if (!canvasContext) return

        const {mainCtx, dimensions: cropDimensions} = canvasContext

        if (cropper.mode === CONSTANTS.EDITOR_MODES.CROP) {
            renderCrop({
                mainCtx,
                img: img,
                position,
                dimensions,
                cropParams: cropDimensions,
                cropDimensions: cropper.cropState.dimensions,
                setCropParams: cropper.setCropParams,
            })
        } else {
            renderTile({
                mainCtx,
                img: img,
                position,
                dimensions,
                cropParams: cropDimensions,
                cropDimensions: cropper.cropState.dimensions,
                zoom: cropper.imageState.zoom,
                setCropParams: cropper.setCropParams,
            })
        }
    }

    useEffect(() => {
        return () => {
            if (animationIdRef.current) {
                cancelAnimationFrame(animationIdRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (!cropper) return

        cropper.setImageState({
            position: {x: 0, y: 31},
        })

        const zoomFactor = cropper.imageState.zoom / 100
        cropper.setRenderData({
            position: {x: 0, y: 31},
            dimensions: {
                width: cropper.imageState.dimensions.width * zoomFactor,
                height: cropper.imageState.dimensions.height * zoomFactor,
            },
        })
    }, [cropper?.mode])

    useEffect(() => {
        if (
            !cropper?.imageState.status ||
            cropper.imageState.status !== "loaded" ||
            !cropper.renderData ||
            !currentImageRef.current
        ) {
            return
        }

        if (animationIdRef.current) {
            cancelAnimationFrame(animationIdRef.current)
            animationIdRef.current = null
        }

        const {position, dimensions} = cropper.renderData

        if (cropper.videoElement && cropper.imageState.videoPlaying) {
            const draw = () => {
                renderFrame(position, dimensions)
                animationIdRef.current = requestAnimationFrame(draw)
            }
            draw()
        } else {
            renderFrame(position, dimensions)
        }
    }, [
        cropper?.imageState.status,
        cropper?.renderData,
        cropper?.cropState.dimensions,
        cropper?.mode,
        cropper?.videoElement,
        cropper?.imageState.zoom,
        cropper?.imageState.videoPlaying,
    ])
}
